import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Location } from 'react-router-dom';

interface PageParameterConfig {
    /** The page path to monitor (e.g., '/stowage') */
    pagePath: string;
    /** The query parameters to preserve for this page */
    parameters: string[];
    /** The session storage key to use for storing parameters */
    storageKey: string;
    /** Parameters that should always be preserved across all pages */
    globalParameters?: string[];
}

interface UsePageParameterPersistenceProps {
    configs: PageParameterConfig[];
}

export const usePageParameterPersistence = ({ configs }: UsePageParameterPersistenceProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const locationRef = useRef(location);
    const justRestoredRef = useRef(false);
    locationRef.current = location;

    const saveParameters = useCallback(
        (locationToSave: Location) => {
            // Don't save if we just restored parameters
            if (justRestoredRef.current) {
                justRestoredRef.current = false;
                return;
            }

            const pageConfig = configs.find((config) => config.pagePath === locationToSave.pathname);

            if (pageConfig) {
                const params = new URLSearchParams(window.location.search);
                const parametersToSave: [string, string][] = [];

                // Prioritize global parameters to ensure they appear first
                const allParamsToConsider = [...(pageConfig.globalParameters || []), ...pageConfig.parameters];
                const uniqueParams = Array.from(new Set(allParamsToConsider)); // Ensure no duplicates

                uniqueParams.forEach((param) => {
                    const value = params.get(param);
                    if (value) {
                        parametersToSave.push([param, value]);
                    }
                });

                if (parametersToSave.length > 0) {
                    sessionStorage.setItem(pageConfig.storageKey, JSON.stringify(parametersToSave));
                }
            }
        },
        [configs]
    );

    const restoreParameters = useCallback(
        (locationToRestore: Location) => {
            const currentPageConfig = configs.find((config) => config.pagePath === locationToRestore.pathname);

            if (!currentPageConfig) {
                return;
            }

            const currentParams = new URLSearchParams(locationToRestore.search);
            const currentImoNumber = localStorage.getItem('selectedShipImoNumber');
            const hasExistingParams = currentPageConfig.parameters.some((param) => currentParams.has(param));
            const savedParamsString = sessionStorage.getItem(currentPageConfig.storageKey);
            const savedImoNumber = savedParamsString ? JSON.parse(savedParamsString)?.find(([key]) => key === 'imoNumber')?.[1] : null;

            // if the user has changed selected vessel after these parameters were saved, ignore them
            if (hasExistingParams || (currentImoNumber && savedImoNumber && currentImoNumber !== savedImoNumber)) {
                return;
            }

            if (savedParamsString) {
                try {
                    const savedParams: [string, string][] = JSON.parse(savedParamsString);
                    const newSearch = new URLSearchParams(savedParams).toString();

                    justRestoredRef.current = true;
                    navigate(`${locationToRestore.pathname}?${newSearch}`, { replace: true });

                    // Remove after navigation to prevent re-restoration
                    setTimeout(() => {
                        sessionStorage.removeItem(currentPageConfig.storageKey);
                    }, 0);
                } catch (error) {
                    console.error(`Failed to restore parameters for ${currentPageConfig.pagePath}:`, error);
                    sessionStorage.removeItem(currentPageConfig.storageKey);
                }
            }
        },
        [configs]
    );

    useEffect(() => {
        restoreParameters(location);

        // The cleanup function will run when location changes, capturing the location
        // from the previous render in its closure. This is where we save parameters.
        return () => {
            saveParameters(locationRef.current);
        };
    }, [location]);

    return {};
};
